import { compact, sortBy } from 'lodash';
import {
  ExactMedicalHistoryQuestionType,
  IExactMedicalHistoryQuestion,
} from '../source/entities/patient-medical-histories';

export enum ExactMedicalHistoryEntryKind {
  Section = 'section',
  Boolean = 'boolean',
  Text = 'text',
}

export interface IExactMedicalHistorySectionEntry {
  kind: ExactMedicalHistoryEntryKind.Section;
  label: string;
}

export interface IExactMedicalHistoryBooleanEntry {
  kind: ExactMedicalHistoryEntryKind.Boolean;
  label: string;
  answer: boolean;
}

export interface IExactMedicalHistoryTextEntry {
  kind: ExactMedicalHistoryEntryKind.Text;
  label: string;
  value: string;
}

export type ExactMedicalHistoryEntry =
  | IExactMedicalHistorySectionEntry
  | IExactMedicalHistoryBooleanEntry
  | IExactMedicalHistoryTextEntry;

const labelOverrides: Record<string, string> = {
  HIV: 'H.I.V.',
  GAExperience: 'G.A. Experience',
};

const containsWhitespace = /\s/;
const lowerUpperBoundary = /([a-z0-9])([A-Z])/g;
const acronymWordBoundary = /([A-Z]+)([A-Z][a-z])/g;
const escapedLineBreaks = /\\r\\n|\\r|\\n/g;
const carriageReturns = /\r\n|\r/g;

/**
 * Renders one Exact medical history submission as form entries.
 *
 * Exact has two form versions — one spelled out with section headings, one camel
 * case with none. Both render verbatim rather than mapping to a shared vocabulary:
 * the migrated document records the form the patient was actually asked.
 */
export class ExactMedicalHistoryForm {
  static toEntries(
    questions: IExactMedicalHistoryQuestion[]
  ): ExactMedicalHistoryEntry[] {
    const ordered = sortBy(questions, (question) => question.question_order);
    const booleanLabels = ExactMedicalHistoryForm._booleanLabels(questions);
    return compact(
      ordered.map((question) =>
        ExactMedicalHistoryForm._toEntry(question, booleanLabels)
      )
    );
  }

  static toLabel(questionText: string): string {
    const trimmed = questionText.trim();
    const override = labelOverrides[trimmed];
    if (override) {
      return override;
    }
    if (containsWhitespace.test(trimmed)) {
      return trimmed;
    }

    // Each match consumes both boundary chars, so `$1 $2` puts them back with a space between.
    return trimmed
      .replace(lowerUpperBoundary, '$1 $2')
      .replace(acronymWordBoundary, '$1 $2');
  }

  /** Exact exports line breaks both as real carriage returns and as unescaped `\r\n` text. */
  static normaliseText(text: string): string {
    return text
      .replace(escapedLineBreaks, '\n')
      .replace(carriageReturns, '\n')
      .trim();
  }

  private static _booleanLabels(
    questions: IExactMedicalHistoryQuestion[]
  ): Set<string> {
    return new Set(
      questions
        .filter(
          (question) =>
            question.question_type === ExactMedicalHistoryQuestionType.Bool
        )
        .map((question) =>
          ExactMedicalHistoryForm.toLabel(question.question_text)
        )
    );
  }

  private static _toEntry(
    question: IExactMedicalHistoryQuestion,
    booleanLabels: Set<string>
  ): ExactMedicalHistoryEntry | undefined {
    switch (question.question_type) {
      case ExactMedicalHistoryQuestionType.Bool:
        return {
          kind: ExactMedicalHistoryEntryKind.Boolean,
          label: ExactMedicalHistoryForm.toLabel(question.question_text),
          answer: question.answer_is_yes ?? false,
        };
      case ExactMedicalHistoryQuestionType.Text:
        return ExactMedicalHistoryForm._toTextEntry(question);
      default:
        return question.answer_text
          ? ExactMedicalHistoryForm._toTextEntry(question)
          : ExactMedicalHistoryForm._toSectionEntry(question, booleanLabels);
    }
  }

  // Older rows have answer typed into label as `Label: answer`. Trailing text is
  // clinical content, so keep it above the answer proper.
  private static _toTextEntry(
    question: IExactMedicalHistoryQuestion
  ): IExactMedicalHistoryTextEntry | undefined {
    const separatorIndex = question.question_text.indexOf(':');
    const hasEmbeddedAnswer = separatorIndex !== -1;
    const labelText = hasEmbeddedAnswer
      ? question.question_text.slice(0, separatorIndex)
      : question.question_text;
    const embeddedAnswer = hasEmbeddedAnswer
      ? question.question_text.slice(separatorIndex + 1)
      : '';

    const value = compact([
      ExactMedicalHistoryForm.normaliseText(embeddedAnswer),
      ExactMedicalHistoryForm.normaliseText(question.answer_text ?? ''),
    ]).join('\n');

    if (!value) {
      return;
    }

    return {
      kind: ExactMedicalHistoryEntryKind.Text,
      label: ExactMedicalHistoryForm.toLabel(labelText),
      value,
    };
  }

  private static _toSectionEntry(
    question: IExactMedicalHistoryQuestion,
    booleanLabels: Set<string>
  ): IExactMedicalHistorySectionEntry | undefined {
    const label = ExactMedicalHistoryForm.toLabel(question.question_text);
    if (!label || booleanLabels.has(label)) {
      return;
    }
    return { kind: ExactMedicalHistoryEntryKind.Section, label };
  }
}
